import { useEffect, useState, useCallback } from 'react'
import { useTelegram } from './use-telegram'

type HomeScreenStatus = 'unsupported' | 'unknown' | 'added' | 'missed'

/**
 * Хук для работы с добавлением на домашний экран (Bot API 8.0+)
 * 
 * Позволяет проверить статус и предложить пользователю добавить
 * ярлык Mini App на домашний экран устройства.
 * 
 * @example
 * ```tsx
 * const { status, canAdd, addToHomeScreen, loading } = useHomescreen()
 * 
 * // Показать баннер если пользователь ещё не добавил
 * if (canAdd) {
 *   return (
 *     <Banner
 *       title="Быстрый доступ"
 *       description="Добавьте приложение на домашний экран"
 *       onAction={addToHomeScreen}
 *     />
 *   )
 * }
 * ```
 */
export function useHomescreen() {
  const telegram = useTelegram()
  const [status, setStatus] = useState<HomeScreenStatus>('unknown')
  const [loading, setLoading] = useState(true)

  // Проверяем статус при монтировании
  useEffect(() => {
    let cancelled = false

    const checkStatus = async () => {
      try {
        const currentStatus = await telegram.checkHomeScreenStatus()
        if (!cancelled) {
          setStatus(currentStatus)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    checkStatus()

    return () => {
      cancelled = true
    }
  }, [])

  // Добавить на домашний экран
  const addToHomeScreen = useCallback(() => {
    telegram.hapticFeedback.notificationOccurred('success')
    telegram.addToHomeScreen()
  }, [telegram])

  // Можно ли предложить добавить
  const canAdd = status === 'missed' || status === 'unknown'

  // Поддерживается ли функция
  const isSupported = status !== 'unsupported'

  return {
    /** Текущий статус: 'unsupported' | 'unknown' | 'added' | 'missed' */
    status,
    /** Можно ли предложить добавить (не добавлено и поддерживается) */
    canAdd,
    /** Поддерживается ли функция на устройстве */
    isSupported,
    /** Добавить на домашний экран */
    addToHomeScreen,
    /** Загрузка статуса */
    loading,
  }
}

